import immutable from 'immutable'
import jot from '../jot'
import jotUtil from '../util/jot.util'
import _ from 'lodash'

import {socketUtil} from './socket.util'

import {
    SOCKET_CONNECT,
    SOCKET_DISCONNECT,
    SOCKET_QUEUE_COMMAND,
    SOCKET_RECV_UPDATE,
    SOCKET_REJECT,
    PRESENCE_LIST,
    PRESENCE_JOIN,
    PRESENCE_UPDATE,
    PRESENCE_LEAVE,
    socketUpdate,
    SET_COMMIT_MSG
} from './socket.action'

import {ADD_LEG, ACTIVATE_LEG} from '../leg/leg.action'
import {CHANGE_DETAILS_SECTION} from '../details/details.action'
import presence from './socket.presence'


const queueCommand = (state, action) => {
    if (!action.socket || _.isNull(action.op)) {
        // not going to happen
        return state
    }

    // update the history
    state = state.updateIn(['socket', 'history'], (history) => {
        if (!history.ops) {
            history = new jot.LIST([history])
        }
        return history.compose(action.op)
    })

    if (_.isNil(state.getIn(['socket', 'awaiting']))) {
        state = state.setIn(['socket', 'awaiting'], {op: action.op, reverseOp: action.reverseOp})

        let updateAction = socketUpdate(action.op.serialize(), action.revision, action.author)
        let [params, message] = socketUtil.prepareAction(updateAction)

        // trying to add the message
        let commitMsg = state.getIn(['socket', 'commitMsg'])
        if (commitMsg != null) {
            message.commitMsg = commitMsg
        } else {
            message.commitMsg = 'UPDATED ITINERARY'
        }

        // reset the commit msg to null
        state = state.setIn(['socket', 'commitMsg'], null)

        action.socket.emit(params.room, message)
    } else {
        state = state.updateIn(['socket', 'buffer'], (buffer) => {
            if (_.isNil(buffer)) {
                buffer = {
                    op: new jot.LIST([]),
                    reverseOp: new jot.LIST([])
                }
            }

            buffer.op = buffer.op.compose(action.op)
            buffer.reverseOp = buffer.reverseOp.compose(action.reverseOp)

            return buffer
        })
    }

    return state
}


const getBuffer = (state) => {
    let buffer = state.getIn(['socket', 'buffer'])

    if (!buffer || !buffer.op.ops || !buffer.op.opts.length) {
        return null
    }

    return buffer
}


const sendBuffer = (state, buffer) => {
    state = state.setIn(['socket', 'awaiting'], buffer)

    if (_.isNil(buffer)) {
        return state
    }

    let updateAction = socketUpdate(buffer.op.serialize(), state.get('revision'), state.get('clientSessionId'))
    let [params, message] = socketUtil.prepareAction(updateAction)

    // trying to add the message
    let commitMsg = state.getIn(['socket', 'commitMsg'])
    if (commitMsg != null) {
        message.commitMsg = commitMsg
    } else {
        message.commitMsg = 'UPDATED ITINERARY'
    }

    // reset the commit msg to null
    state = state.setIn(['socket', 'commitMsg'], null)

    action.socket.emit(params.room, message)

    state = state.setIn(['socket', 'buffer'], null)
    return state
}


const applyServerJOT = (state, action) => {
    if (_.isNull(action.op)) {
        return state
    }

    let op = jot.deserialize(action.op)
    state = state.update('revision', (revision) => revision + 1)

    if (action.author === state.get('clientSessionId')) {
        let buffer = getBuffer(state)
        state = sendBuffer(state, buffer)
    } else {
        let awaiting = state.getIn(['socket', 'awaiting'])
        let history = state.getIn(['socket', 'history'])
        if (!history.ops) {
            history = new jot.LIST([history])
        }

        if (!_.isNull(awaiting)) {
            awaiting.reverseOp = awaiting.reverseOp.rebase(op)

            let tempOp = op.rebase(awaiting.op)
            op = tempOp ? tempOp : op

            tempOp = op.rebase(history)
            op = tempOp ? tempOp : op
        }

        let doc = state.get('document').toJS()
        doc = op.apply(doc)

        state = state.set('document', immutable.fromJS(doc))
        state = state.updateIn(['socket', 'history'], (history) => {
            return history.compose(op)
        })
    }

    return state
}

const rejectServerJOT = (state, action) => {
    // got a reject message. need to reverse the awaiting message
    let awaiting = state.getIn(['socket', 'awaiting'])
    let reverseOp = awaiting.reverseOp

    if (reverseOp) {
        let doc = state.get('document').toJS()
        doc = awaiting.reverseOp.apply(doc)
        state = state.set('document', immutable.fromJS(doc))
    }

    // get the buffer into the awaiting slot and send it out
    let buffer = getBuffer(state)
    state = sendBuffer(state, buffer)

    return state
}


export default (state, action) => {
    switch (action.type) {
        case SOCKET_CONNECT:
            return state
        case SOCKET_DISCONNECT:
            return state.setIn(['socket', 'presence'], immutable.Map())
        case SOCKET_QUEUE_COMMAND:
            return queueCommand(state, action)
        case SOCKET_RECV_UPDATE:
            // adding special logic for handling history here
            let existingCommits = state.get('history')
            let newHistory = {
                commit_message: action.commitMsg,
                commit_hash: action.commitHash,
                traveler_guid: action.traveler,
                created_at: action.createdAt
            }
            existingCommits = existingCommits.push(immutable.fromJS(newHistory))
            state = state.set('history', existingCommits)

            return applyServerJOT(state, action)
        case SOCKET_REJECT:
            return rejectServerJOT(state, action)
        case PRESENCE_LIST:
            let presences = _.reduce(action.presences, (acc, presence) => {
                acc[presence.clientId] = presence
                return acc
            }, {})

            return state.setIn(['socket', 'presence'], immutable.fromJS(presences))
        case PRESENCE_JOIN:
        case PRESENCE_UPDATE:
            return state.setIn(['socket', 'presence', action.presence.clientId], immutable.fromJS(action.presence))
        case PRESENCE_LEAVE:
            return state.deleteIn(['socket', 'presence', action.clientId])
        case ADD_LEG:
        case ACTIVATE_LEG:
            let clientId = state.get('clientSessionId')
            return presence.update(state, clientId, {
                leg: action.leg ? action.leg.get('id') : null,
                subsecton: null
            })
        case CHANGE_DETAILS_SECTION:
            clientId = state.get('clientSessionId')
            return presence.update(state, clientId, {
                subsection: action.section
            })
        case SET_COMMIT_MSG:
            let curMsg = state.getIn(['socket', 'commitMsg'])
            if (curMsg == null) {
                return state.setIn(['socket', 'commitMsg'], action.msg)
            } else {
                return state
            }
        default:
            return state
    }
}
